// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import {
    ComboBox,
    IComboBoxStyles,
    IDropdownOption,
} from 'office-ui-fabric-react';
import { TextField } from 'office-ui-fabric-react/lib/TextField';
import { useTranslation } from 'react-i18next';

import { postalCodeValidation } from './../../helpers/postalCodePatternPerCountry';
import { provinceValidation } from './../../helpers/provinceRequiredPerCountry';

import colors from './../../styles/_colors.scss';

interface AddressFieldsProps {
    readonly?: boolean;
    disabled: boolean;
    isWalkInCenter: boolean;
    address1?: string;
    address2?: string;
    city?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    language?: string;
    phoneNumber?: string;
    postalCode?: string;
    country: string;
    stateOrProvince?: string;
    languageOptions: IDropdownOption[];
    countryOptions: IDropdownOption[];
    provincesOptions: IDropdownOption[];
    onAddressInputChanges?: (inputData: any) => void;
    isEnterpriseCustomer: boolean;
    vatId: string;
    customerPO: string;
    validation: {
        email?: string;
        firstName?: string;
        lastName?: string;
        phoneNumber?: string;
        address1?: string;
        city?: string;
        country?: string;
        postalCode?: string;
        stateOrProvince?: string;
    };
}

const dropdownStyles: Partial<IComboBoxStyles> = {
    container: {
        minWidth: 100,
    },
    errorMessage: {
        fontSize: 12,
    },
    optionsContainerWrapper: {
        maxHeight: 350,
    },
};

const MAX_CHARACTER_LIMIT = 35;

export default function AddressFields(props: AddressFieldsProps) {
    const { t } = useTranslation();

    dropdownStyles.label = {
        color: props.readonly ? colors.waikawaGray : colors.black,
    };

    const [postalCodeRequired, setPostalCodeRequired] = useState(false);
    const [provinceRequired, setProvinceRequired] = useState(false);
    const [addressState, setAddressState] = useState({
        firstName: props.firstName,
        lastName: props.lastName,
        phoneNumber: props.phoneNumber,
        language: props.language,
        postalCode: props.postalCode,
        address1: props.address1,
        address2: props.address2,
        email: props.email,
        city: props.city,
        country: props.country,
        stateOrProvince: props.stateOrProvince,
        vatId: props.vatId,
        customerPO: props.customerPO,
        validation: {},
    });

    useEffect(() => {
        setAddressState({
            ...addressState,
            email: props.email,
            firstName: props.firstName,
            lastName: props.lastName,
            phoneNumber: props.phoneNumber,
            language: props.language,
            address1: props.address1,
            address2: props.address2,
            postalCode: props.postalCode,
            city: props.city,
            country: props.country,
            stateOrProvince: props.stateOrProvince,
            vatId: props.vatId,
            customerPO: props.customerPO,
        });
    }, [
        props.email,
        props.firstName,
        props.lastName,
        props.phoneNumber,
        props.language,
        props.address1,
        props.address2,
        props.postalCode,
        props.city,
        props.country,
        props.stateOrProvince,
        props.vatId,
        props.customerPO,
        props.validation,
    ]);

    useEffect(() => {
        props.onAddressInputChanges &&
            props.onAddressInputChanges(addressState);
    }, [
        addressState.email,
        addressState.firstName,
        addressState.lastName,
        addressState.language,
        addressState.phoneNumber,
        addressState.address1,
        addressState.address2,
        addressState.postalCode,
        addressState.city,
        addressState.country,
        addressState.stateOrProvince,
        addressState.vatId,
        addressState.customerPO,
        addressState.validation,
    ]);

    function handleAddressChange(
        event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        newValue?: string
    ) {
        const { name } = event.target as HTMLInputElement;
        setAddressState({ ...addressState, [name]: newValue });
    }

    function languageOnChange(event: any, selectedOption?: IDropdownOption) {
        if (selectedOption) {
            setAddressState({
                ...addressState,
                ['language']: (selectedOption.key as unknown) as string,
            });
        }
    }

    function countryOnChange(event: any, selectedOption?: IDropdownOption) {
        if (selectedOption) {
            const selectedCountry = (selectedOption.key as unknown) as string;
            setAddressState({ ...addressState, ['country']: selectedCountry, postalCode: '', city: '' });

            const postalCodeRequired =
                postalCodeValidation[selectedCountry.toLowerCase()];
            if (postalCodeRequired) {
                setPostalCodeRequired(true);
            } else {
                setPostalCodeRequired(false);
            }

            const provinceRequired =
                provinceValidation[selectedCountry.toLowerCase()];
            if (provinceRequired && provinceRequired.required) {
                setProvinceRequired(true);
            } else {
                setProvinceRequired(false);
            }
        }
    }

    function provinceOnChange(event: any, selectedOption?: IDropdownOption) {
        if (selectedOption) {
            setAddressState({
                ...addressState,
                ['stateOrProvince']: (selectedOption.key as unknown) as string,
            });
        }
    }

    return (
        <>
            <div className="ms-Grid-row">
                <div className={clsx('ms-Grid-col', 'ms-sm6')}>
                    <TextField
                        ariaLabel={t('email')}
                        disabled={props.isWalkInCenter || props.disabled || props.readonly}
                        errorMessage={props.validation.email}
                        label={t('email')}
                        name="email"
                        onChange={handleAddressChange}
                        required={true}
                        validateOnFocusOut={true}
                        value={addressState.email}
                    />
                </div>
                <div className={clsx('ms-Grid-col', 'ms-sm3')}>
                    <TextField
                        name="firstName"
                        disabled={props.isWalkInCenter || props.disabled || props.readonly}
                        required={true}
                        label={props.isEnterpriseCustomer === true ? t('first_name_org_label') : t('first_name')}
                        errorMessage={props.validation.firstName}
                        onChange={handleAddressChange}
                        value={addressState.firstName}
                        ariaLabel={props.isEnterpriseCustomer === true ? t('first_name_org_label') : t('first_name')}
                    />
                </div>
                <div className={clsx('ms-Grid-col', 'ms-sm3')}>
                    <TextField
                        name="lastName"
                        disabled={props.isWalkInCenter || props.disabled || props.readonly}
                        required={true}
                        label={props.isEnterpriseCustomer === true ? t('last_name_org_label') : t('last_name')}
                        errorMessage={props.validation.lastName}
                        onChange={handleAddressChange}
                        value={addressState.lastName}
                        ariaLabel={props.isEnterpriseCustomer === true ? t('last_name_org_label') : t('last_name')}
                    />
                </div>
            </div>

            <div className="ms-Grid-row">
                <div className={clsx('ms-Grid-col', 'ms-sm6')}>
                    <TextField
                        ariaLabel={t('phone_number')}
                        disabled={props.isWalkInCenter || props.disabled || props.readonly}
                        label={t('phone_number')}
                        name="phoneNumber"
                        onChange={handleAddressChange}
                        errorMessage={props.validation.phoneNumber}
                        required={true}
                        validateOnFocusOut={true}
                        value={addressState.phoneNumber}
                    />
                </div>
                <div className={clsx('ms-Grid-col', 'ms-sm6')}>
                    <TextField
                        name="address1"
                        required={true}
                        label={t('address_1')}
                        errorMessage={props.validation.address1}
                        disabled={props.isWalkInCenter || props.disabled || props.readonly}
                        onChange={handleAddressChange}
                        value={addressState.address1}
                        maxLength={MAX_CHARACTER_LIMIT}
                        ariaLabel={t('address_1')}
                    />
                </div>
            </div>

            <div className="ms-Grid-row">
                <div className={clsx('ms-Grid-col', 'ms-sm6')}>
                    <ComboBox
                        autoComplete="on"
                        label={t('language')}
                        ariaLabel={t('language')}
                        disabled={props.isWalkInCenter || props.disabled || props.readonly}
                        selectedKey={addressState.language}
                        options={props.languageOptions}
                        styles={dropdownStyles}
                        onChange={languageOnChange}
                    />
                </div>
                <div className={clsx('ms-Grid-col', 'ms-sm6')}>
                    <TextField
                        name="address2"
                        label={t('address_2')}
                        disabled={props.isWalkInCenter || props.disabled || props.readonly}
                        onChange={handleAddressChange}
                        value={addressState.address2}
                        defaultValue={t('address_2_default_placeholder')}
                        ariaLabel={t('address_2')}
                        maxLength={MAX_CHARACTER_LIMIT}
                    />
                </div>
            </div>

            <div className="ms-Grid-row">
                <div className={clsx('ms-Grid-col', 'ms-sm6')}>
                    <ComboBox
                        autoComplete="on"
                        label={t('country')}
                        ariaLabel={t('country')}
                        disabled={props.isWalkInCenter || props.disabled || props.readonly}
                        selectedKey={addressState.country}
                        errorMessage={props.validation.country}
                        required={true}
                        options={props.countryOptions}
                        styles={dropdownStyles}
                        onChange={countryOnChange}
                    />
                </div>
                <div className={clsx('ms-Grid-col', 'ms-sm3')}>
                    <TextField
                        name="city"
                        required={true}
                        label={t('city')}
                        errorMessage={props.validation.city}
                        disabled={props.isWalkInCenter || props.disabled || props.readonly}
                        onChange={handleAddressChange}
                        value={addressState.city}
                        ariaLabel={t('city')}
                        maxLength={MAX_CHARACTER_LIMIT}
                    />
                </div>
                <div className={clsx('ms-Grid-col', 'ms-sm3')}>
                    <ComboBox
                        autoComplete="on"
                        label={t('state_province')}
                        ariaLabel={t('state_province')}
                        errorMessage={props.validation.stateOrProvince}
                        selectedKey={addressState.stateOrProvince}
                        disabled={props.isWalkInCenter || props.disabled || props.readonly}
                        onChange={provinceOnChange}
                        options={props.provincesOptions}
                        required={provinceRequired}
                        styles={dropdownStyles}
                    />
                </div>
            </div>

            <div className="ms-Grid-row">
                <div className={clsx('ms-Grid-col', 'ms-sm6')}>
                    <TextField
                        name="postalCode"
                        label={t('zip_code')}
                        required={postalCodeRequired}
                        errorMessage={props.validation.postalCode}
                        disabled={props.isWalkInCenter || props.disabled || props.readonly}
                        onChange={handleAddressChange}
                        value={addressState.postalCode}
                        type="text"
                        ariaLabel={t('zip_code')}
                    />
                </div>
                <div className={clsx('ms-Grid-col', 'ms-sm3')}>
                    <TextField
                        name="vatId"
                        label={t('vat_id')}
                        disabled={props.isWalkInCenter || props.disabled || props.readonly}
                        onChange={handleAddressChange}
                        value={addressState.vatId}
                        ariaLabel={t('vat_id')}
                        maxLength={MAX_CHARACTER_LIMIT}
                    />
                </div>
                <div className={clsx('ms-Grid-col', 'ms-sm3')}>
                    <TextField
                        name="customerPO"
                        label={t('customer_po')}
                        disabled={props.isWalkInCenter || props.disabled || props.readonly}
                        onChange={handleAddressChange}
                        value={addressState.customerPO}
                        ariaLabel={t('customer_po')}
                        maxLength={MAX_CHARACTER_LIMIT}
                    />
                </div>
            </div>
        </>
    );
}
